import React, { useState } from 'react';
import axios from 'axios';
import '../css/PartnerRegistration.css';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';

const PartnerRegistration = () => {
  const [formData, setFormData] = useState({
    roleType: '',
    buyerType: '',
    companyName: '',
    businessType: '',
    gstin: '',
    panNumber: '',
    yearOfEstablishment: '',
    website: '',
    contactName: '',
    designation: '',
    mobileNumber: '',
    alternateMobile: '',
    whatsAppNumber: '',
    emailAddress: '',
    birthday: '',
    anniversary: '',
    
    // Address Fields
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',

    // Bank Details
    bankAccountNo: '',
    bankIfsc: '',
    bankAccountHolder: '',
    upiId: '',

    // Role Specific Fields
    mainCategory: '',
    businessExperience: '',
    expectedMonthlyPurchase: '',
    territoryCovered: '',
    preferredBrands: [], // Array for multiple selection
  });

  const [brandFile, setBrandFile] = useState(null); // For Vendor authorization file
  const [message, setMessage] = useState(null); // Changed to null initially to render JSX cleanly
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Static lists
  const categoriesList = ['Electronics', 'Clothing & Apparel', 'Footwear', 'Home Decor', 'Beauty & Cosmetics', 'Kitchen Appliances'];
  const brandsList = ['Mobility', 'NCN', 'Imaging'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (brand) => {
    let updatedBrands = [...formData.preferredBrands];
    if (updatedBrands.includes(brand)) {
      updatedBrands = updatedBrands.filter((b) => b !== brand);
    } else {
      updatedBrands.push(brand);
    }
    setFormData({ ...formData, preferredBrands: updatedBrands });
  };

  const handleFileChange = (e) => {
    setBrandFile(e.target.files[0]);
  };

  // Helper variables to check current selected role
  const isVendor = formData.roleType === 'Vendor';
  const isBuyer = formData.roleType === 'Buyer';
  const isSalesAgent = isBuyer && formData.buyerType === 'Sales Agent';
  
  const isWholesaleLoyal = isBuyer && formData.buyerType === 'Wholesale Loyal Partner Club';
  const isBrandLoyal = isBuyer && formData.buyerType === 'Brand Loyal Partner Club';
  const isLoyalClub = isWholesaleLoyal || isBrandLoyal;
  const isBrandDistributor = isBuyer && formData.buyerType === 'Brand Distributor';

  // 🚀 RAZORPAY PAYMENT TRIGGER FUNCTION FOR LOYAL CLUB
  const handleRazorpayPayment = async (sanitizedData, dataObj) => {
    try {
      const membershipAmount = 999; // Membership fee
      const orderRes = await axios.post(`${API_BASE}/api/create-razorpay-order`, {
        amount: membershipAmount
      });

      if (!orderRes.data.success) {
        setError("Payment initialization failed.");
        return;
      }

      const { razorpayOrderId, amount } = orderRes.data;

      const options = {
        key: "rzp_test_TGTuUtY0PiWmUL", //   Test Key ID 
        amount: amount,
        currency: "INR",
        name: "2look by NCN",
        description: "Loyal Partner Club Membership Fee",
        order_id: razorpayOrderId,
        handler: async function (response) {
          try {
            dataObj.append('razorpay_payment_id', response.razorpay_payment_id);
            dataObj.append('razorpay_order_id', response.razorpay_order_id);
            dataObj.append('razorpay_signature', response.razorpay_signature);

            const verifyRes = await axios.post(`${API_BASE}/api/partners/register-with-payment`, dataObj, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (verifyRes.data.success) {
              handleSuccessfulRegistration(verifyRes.data.credentials);
            }
          } catch (err) {
            setError(err.response?.data?.message || 'Payment verification failed.');
          }
        },
        prefill: {
          name: formData.contactName,
          email: formData.emailAddress,
          contact: formData.mobileNumber
        },
        theme: {
          color: "#2563eb"
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

    } catch (err) {
      setError("Could not connect to payment gateway.");
    }
  };

  const handleSuccessfulRegistration = (credentials) => {
    const { username, userCode } = credentials || {};
    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);

    setMessage(
      <div style={{ backgroundColor: '#fff7ed', border: '2px solid #ea580c', padding: '20px', borderRadius: '12px', marginBottom: '25px', boxShadow: '0 4px 12px rgba(234, 88, 12, 0.08)' }}>
        <h3 style={{ color: '#c2410c', margin: '0 0 8px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          🎉 Registration & Payment Successful!
        </h3>
        <p><strong>Membership Valid Until:</strong> {expiryDate.toLocaleDateString()}</p>
        
        <div style={{ backgroundColor: '#fef2f2', borderLeft: '4px solid #ef4444', padding: '10px 12px', borderRadius: '4px', marginBottom: '15px', fontSize: '12.5px', color: '#991b1b', fontWeight: '500' }}>
          ⚠️ <strong>MANDATORY NOTICE:</strong> These details are extremely important! Please save or write them down in a safe place right away. You will need this Username and Secret User Code to log in.
        </div>

        <div style={{ backgroundColor: '#ffffff', padding: '15px', borderRadius: '8px', border: '1px solid #fed7aa' }}>
          <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#374151' }}>
            <strong>👤 Your Unique Username:</strong> <span style={{ color: '#2563eb', fontFamily: 'monospace', fontWeight: 'bold', fontSize: '16px', backgroundColor: '#eff6ff', padding: '2px 6px', borderRadius: '4px' }}>{username || 'N/A'}</span>
          </p>
          <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
            <strong>🔑 Your Secret User Code:</strong> <span style={{ color: '#dc2626', fontFamily: 'monospace', fontWeight: 'bold', fontSize: '16px', backgroundColor: '#fef2f2', padding: '2px 6px', borderRadius: '4px', letterSpacing: '1px' }}>{userCode || 'N/A'}</span>
          </p>
        </div>
      </div>
    );
    
    setBrandFile(null);
    setFormData({
      roleType: '', buyerType: '', companyName: '', businessType: '', gstin: '', panNumber: '',
      yearOfEstablishment: '', website: '', contactName: '', designation: '', mobileNumber: '',
      alternateMobile: '', whatsAppNumber: '', emailAddress: '', birthday: '', anniversary: '',
      addressLine1: '', addressLine2: '', city: '', state: '', pincode: '',
      bankAccountNo: '', bankIfsc: '', bankAccountHolder: '', upiId: '',
      mainCategory: '', businessExperience: '', expectedMonthlyPurchase: '', territoryCovered: '',
      preferredBrands: [], 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError('');

    let sanitizedData = { ...formData };

    if (!isVendor && !isSalesAgent) {
      sanitizedData.bankAccountHolder = '';
      sanitizedData.bankAccountNo = '';
      sanitizedData.bankIfsc = '';
      sanitizedData.upiId = '';
    }
    if (!isVendor) sanitizedData.mainCategory = '';
    if (!isVendor && !isBrandDistributor) sanitizedData.businessExperience = '';
    if (!isBrandDistributor) sanitizedData.territoryCovered = '';
    if (!isLoyalClub) sanitizedData.expectedMonthlyPurchase = '';
    if (!isBrandLoyal) sanitizedData.preferredBrands = [];

    try {
      // 1. 🔍 Pehle Duplicate Check API Call Karein
      const checkRes = await axios.post(`${API_BASE}/api/partners/check-duplicate`, {
        gstin: !isSalesAgent ? sanitizedData.gstin : null,
        panNumber: sanitizedData.panNumber,
        mobileNumber: sanitizedData.mobileNumber,
        emailAddress: sanitizedData.emailAddress,
        bankAccountNo: (isVendor || isSalesAgent) ? sanitizedData.bankAccountNo : null
      });

      if (!checkRes.data.success) {
        setError(checkRes.data.message || 'Duplicate details found.');
        return; 
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Validation check failed.');
      return;
    }

    const data = new FormData();
    Object.keys(sanitizedData).forEach((key) => {
      if (key === 'preferredBrands') {
        data.append(key, JSON.stringify(sanitizedData[key]));
      } else {
        data.append(key, sanitizedData[key]);
      }
    });

    if (isVendor && brandFile) {
      data.append('brandAuthorizationLetter', brandFile);
    }

    if (isLoyalClub) {
      handleRazorpayPayment(sanitizedData, data);
    } else {
      try {
        const response = await axios.post(`${API_BASE}/api/partners/register`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data.success) {
          handleSuccessfulRegistration(response.data.credentials);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Something went wrong during sync creation routing.');
      }
    }
  };

  return (
    <div className="registration-master-container">
      {/* ================= LEFT SIDEBAR ================= */}
      <div className="reg-left-sidebar">
        <div className="sidebar-sticky-content">
          <h1>Become Our <span>Partner</span></h1>
          <p className="subtitle">Grow with us. Succeed together.</p>
          <p className="desc-text">
            Join 2look by NCN and expand your business with India's trusted 2look marketplace.
          </p>

          <div className="benefits-list">
            <div className="benefit-item">
              <div className="benefit-icon">👥</div>
              <div className="benefit-info">
                <h4>Wider Reach</h4>
                <p>Access a vast network of resellers, retailers and business buyers across India.</p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">📈</div>
              <div className="benefit-info">
                <h4>Grow Your Business</h4>
                <p>Increase sales and scale your business with our powerful platform and tools.</p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">🛡️</div>
              <div className="benefit-info">
                <h4>Trusted Platform</h4>
                <p>Partner with a reliable and reputed brand backed by NCN.</p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">📢</div>
              <div className="benefit-info">
                <h4>Marketing Support</h4>
                <p>Get co-marketing opportunities and dedicated partner support.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= MAIN FORM REGISTRATION ================= */}
      <div className="reg-main-content">
        <h2>Partner Registration Form</h2>
        <p className="form-info-line">Fill in the details below to register.</p>

        {message && <div>{message}</div>}
        {error && <div style={{ color: '#dc2626', backgroundColor: '#fef2f2', border: '1px solid #fecaca', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '13px' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          
          {/* STEP 0: Select Partner Type */}
          <div className="form-section">
            <span className="section-title">⚙️ Select Partner Type</span>
            <div className="form-grid-three">
              <div className="input-group">
                <label>Partner Type <span className="required">*</span></label>
                <select name="roleType" required value={formData.roleType} onChange={handleChange}>
                  <option value="">Select Partner Type</option>
                  <option value="Vendor">Vendor</option>
                  <option value="Buyer">Buyer</option>
                </select>
              </div>

              {isBuyer && (
                <div className="input-group">
                  <label>Buyer Type <span className="required">*</span></label>
                  <select name="buyerType" required value={formData.buyerType} onChange={handleChange}>
                    <option value="">Select Buyer Type</option>
                    <option value="Wholesale Loyal Partner Club">Wholesale Loyal Partner Club (Premium 👑)</option>
                    <option value="Brand Loyal Partner Club">Brand Loyal Partner Club (Premium 👑)</option>
                    <option value="Brand Distributor">Brand Distributor</option>
                    <option value="Sales Agent">Sales Agent (Commission Based)</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {isLoyalClub && (
            <div style={{ backgroundColor: '#eff6ff', border: '1px dashed #2563eb', padding: '16px', borderRadius: '12px', marginBottom: '25px' }}>
              <h4 style={{ color: '#1e40af', fontSize: '13px', margin: '0 0 8px 0', fontWeight: 'bold' }}>👑 Loyal Partner Club Membership Benefits:</h4>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '11px', color: '#1e3a8a', lineHeight: '1.6' }}>
                <li><strong>Maximum Discounts:</strong> Get much lower prices compared to standard partners.</li>
                <li><strong>First Priority Stock:</strong> Whenever new stock arrives, it goes to you first.</li>
                <li><strong>Exclusive Location Rights:</strong> Orders from your designated area will route directly to you!</li>
              </ul>
            </div>
          )}

          {/* 1. Profile / Business Details Section */}
          <div className="form-section">
            <span className="section-title">
              {isSalesAgent ? '👤 1. Personal & Identity Details' : '🏢 1. Business Details'}
            </span>
            <div className="form-grid-three">
              
              <div className="input-group">
                <label>{isSalesAgent ? 'Full Name (Individual)' : 'Company / Business Name'} <span className="required">*</span></label>
                <input type="text" name="companyName" required placeholder={isSalesAgent ? 'Enter full name' : 'Enter company name'} value={formData.companyName} onChange={handleChange} />
              </div>

              {!isSalesAgent && (
                <div className="input-group">
                  <label>Business Type <span className="required">*</span></label>
                  <select name="businessType" required value={formData.businessType} onChange={handleChange}>
                    <option value="">Select Business Type</option>
                    <option value="Proprietorship">Proprietorship</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Private Ltd">Private Ltd</option>
                  </select>
                </div>
              )}

              {!isSalesAgent && (
                <div className="input-group">
                  <label>GST Number <span className="required">*</span></label>
                  <input type="text" name="gstin" required placeholder="Enter GSTIN" value={formData.gstin} onChange={handleChange} />
                </div>
              )}

              <div className="input-group">
                <label>{isSalesAgent ? 'Individual PAN Number' : 'PAN Number'} <span className="required">*</span></label>
                <input type="text" name="panNumber" required placeholder="Enter PAN Number" value={formData.panNumber} onChange={handleChange} />
              </div>

              {(isVendor || isBrandDistributor || !formData.roleType) && (
                <div className="input-group">
                  <label>{isVendor ? 'Year of Establishment' : 'Years in Business / Brand Exp.'} <span className="required">*</span></label>
                  <input type="text" name="businessExperience" required placeholder="e.g. 5 Years or Select Year" value={formData.businessExperience} onChange={handleChange} />
                </div>
              )}

              {isBrandDistributor && (
                <div className="input-group">
                  <label>Territory Covered <span className="required">*</span></label>
                  <input type="text" name="territoryCovered" required placeholder="Cities/Areas you supply to" value={formData.territoryCovered} onChange={handleChange} />
                </div>
              )}

              {isLoyalClub && (
                <div className="input-group">
                  <label>Expected Monthly Purchase <span className="required">*</span></label>
                  <select name="expectedMonthlyPurchase" required value={formData.expectedMonthlyPurchase} onChange={handleChange}>
                    <option value="">Select Range</option>
                    <option value="1-3 Lacs">1-3 Lacs</option>
                    <option value="3-5 Lacs">3-5 Lacs</option>
                    <option value="5-10 Lacs">5-10 Lacs</option>
                    <option value="10 Lacs+">10 Lacs+</option>
                  </select>
                </div>
              )}

              {isVendor && (
                <div className="input-group">
                  <label>Preference / Main Category <span className="required">*</span></label>
                  <select name="mainCategory" required value={formData.mainCategory} onChange={handleChange}>
                    <option value="">Select Category</option>
                    {categoriesList.map((cat, idx) => (
                      <option key={idx} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {isBrandLoyal && (
              <div style={{ marginTop: '20px' }}>
                <label style={{ fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                  Preferred Brand / Category Interested In <span className="required">*</span>
                </label>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', backgroundColor: '#fafafa', padding: '12px', borderRadius: '8px' }}>
                  {brandsList.map((brand, idx) => (
                    <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={formData.preferredBrands.includes(brand)} 
                        onChange={() => handleCheckboxChange(brand)} 
                      />
                      {brand}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {isVendor && (
              <div style={{ marginTop: '20px' }} className="input-group">
                <label>Brand Authorization Letter / Proof <span className="required">*</span></label>
                <input type="file" required onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png" />
              </div>
            )}
          </div>

          {/* 2. Contact Details Section */}
          <div className="form-section">
            <span className="section-title">👥 2. Contact Details</span>
            <div className="form-grid-three">
              <div className="input-group">
                <label>Contact Person Name <span className="required">*</span></label>
                <input type="text" name="contactName" required placeholder="Enter full name" value={formData.contactName} onChange={handleChange} />
              </div>
              <div className="input-group">
                <label>Designation <span className="required">*</span></label>
                <input type="text" name="designation" required placeholder="Enter designation" value={formData.designation} onChange={handleChange} />
              </div>
              <div className="input-group">
                <label>Mobile Number <span className="required">*</span></label>
                <input type="text" name="mobileNumber" required placeholder="Enter mobile number" value={formData.mobileNumber} onChange={handleChange} />
              </div>
            </div>

            <div className="form-grid-three margin-top-row">
              <div className="input-group">
                <label>WhatsApp Number <span className="required">*</span></label>
                <input type="text" name="whatsAppNumber" required placeholder="WhatsApp number" value={formData.whatsAppNumber} onChange={handleChange} />
              </div>
              <div className="input-group">
                <label>Official Email Address <span className="required">*</span></label>
                <input type="email" name="emailAddress" required placeholder="Enter email address" value={formData.emailAddress} onChange={handleChange} />
              </div>
            </div>
          </div>

          {/* 3. Address Section */}
          <div className="form-section">
            <span className="section-title">
              📍 3. {isVendor ? 'Warehouse / Pickup Address' : isSalesAgent ? 'Current Residential Address' : 'Complete Delivery Address'}
            </span>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '15px', marginTop: '10px' }}>
              <div className="input-group">
                <label>Address Line 1 <span className="required">*</span></label>
                <input type="text" name="addressLine1" required placeholder="House/Gala No, Building, Street" value={formData.addressLine1} onChange={handleChange} />
              </div>
              <div className="input-group">
                <label>City <span className="required">*</span></label>
                <input type="text" name="city" required placeholder="City" value={formData.city} onChange={handleChange} />
              </div>
              <div className="input-group">
                <label>State <span className="required">*</span></label>
                <input type="text" name="state" required placeholder="State" value={formData.state} onChange={handleChange} />
              </div>
              <div className="input-group">
                <label>Pincode <span className="required">*</span></label>
                <input type="text" name="pincode" required placeholder="Pincode" value={formData.pincode} onChange={handleChange} />
              </div>
            </div>
          </div>

          {/* 4. Bank Account Details Section */}
          {(isVendor || isSalesAgent) && (
            <div className="form-section">
              <span className="section-title">🏦 4. Payout / Bank Account Details</span>
              <div className="form-grid-three">
                <div className="input-group">
                  <label>Account Holder Name <span className="required">*</span></label>
                  <input type="text" name="bankAccountHolder" required placeholder="As per bank passbook" value={formData.bankAccountHolder} onChange={handleChange} />
                </div>
                <div className="input-group">
                  <label>Account Number <span className="required">*</span></label>
                  <input type="text" name="bankAccountNo" required placeholder="Enter account number" value={formData.bankAccountNo} onChange={handleChange} />
                </div>
                <div className="input-group">
                  <label>IFSC Code <span className="required">*</span></label>
                  <input type="text" name="bankIfsc" required placeholder="IFSC Code" value={formData.bankIfsc} onChange={handleChange} />
                </div>
              </div>
              {isSalesAgent && (
                <div className="form-grid-three margin-top-row">
                  <div className="input-group">
                    <label>UPI ID <span className="optional">(Optional)</span></label>
                    <input type="text" name="upiId" placeholder="username@upi" value={formData.upiId} onChange={handleChange} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Form Footer Action Layout */}
          <div className="form-footer-action" style={{ marginTop: '20px' }}>
            <label className="checkbox-container">
              <input type="checkbox" required />
              <span>I agree to the <a onClick={()=>navigate('/privacy')}>Terms and Conditions</a>.</span>
            </label>

            <div className="action-buttons">
              <button type="button" className="btn-back">⬅ Back</button>
              <button type="submit" className="btn-submit">
                {isLoyalClub ? 'Pay ₹999 & Register via Razorpay 👑' : 'Submit Form 🚀'}
              </button>
            </div>
          </div>

        </form>
      </div>

      {/* ================= RIGHT SIDEBAR ================= */}
      <div className="reg-right-sidebar">
        <div className="right-top-card">
          <div className="handshake-vector-graphic">
            <span className="graphic-circle">🤝</span>
          </div>
          <h3>Start Your Partnership Journey Today!</h3>
          <p>Our partner team will review your application and get in touch with you shortly.</p>
        </div>

        <div className="right-need-help-card">
          <h4>Need Help?</h4>
          <p>Our partner support team is here to assist you.</p>
          <div className="help-link">
            <span className="help-icon">📞</span>
            <a href="tel:+911142034203">+91 11 4203 4203</a>
          </div>
          <div className="help-link">
            <span className="help-icon">✉️</span>
            <a href="mailto:partner@2lookbyncn.com">partner@2lookbyncn.com</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerRegistration;